/**
 * THE HELP CORPUS — "how do I…", example-first, and now EVERY way to do it.
 *
 * Vinnie's bar (help & learnability initiative; redone 14 Oct 2026): a person who
 * has never seen FlightDeck must be able to find a feature, understand what it
 * does, and do it on their own. A tip that gives ONE route when there are two or
 * three, or that names a control the screen does not have, is worse than none
 * (the "Set budget" correction, 1 Oct). So after a full code audit of the real
 * app, each answer now carries ONE OR MORE WAYS — every genuine route to the
 * same outcome — each with its own succinct step-by-step and its own deep-link.
 *
 * ONE corpus, three surfaces (design rule P9):
 *   - the Help screen lists these grouped by job and shows every way,
 *   - the ⌘K palette offers them as `help` results and coaches/drills the first
 *     (primary) way — the quick jump — and says when there are more,
 *   - and each `steps` line is the same example-first text a point-of-need tip
 *     would use.
 *
 * EVERY WAY IS EXAMPLE-FIRST AND LOCATING, AND CODE-VERIFIED. `steps` is a worked
 * next action in the form "Open X → click Y → choose Z", with the control's real
 * words in quotes — never "X is a feature that…". `target` is the guide's own
 * drill `NavTarget`; on selection the surface drills to the real control and
 * flashes it (`drillTo`/`revealId`). It cannot drift from the product: a control
 * that moves, moves here too.
 *
 * ORDER OF WAYS: the most discoverable / most common route is first, because that
 * is what ⌘K uses. A card field, for instance, leads with the open card ("Edit"
 * → "Save Changes") and then the quick-add shorthand.
 */
import type { View } from './store';

/** One concrete way to do a thing: a named route, its steps, and where it lands. */
export interface HelpWay {
  /** A short label for this route, e.g. "In the open card" or "In the quick-add box". */
  via: string;
  /** The example-first step-by-step for this route. "Open X → click Y → choose Z." */
  steps: string;
  /** Where to take them for this route — the real control, via the guide's drill
   *  model. Omitted for a reference-only route that has no in-app control to jump
   *  to (e.g. a thing you do while signed out); such a way shows no "Take me there". */
  target?: { view: View; reveal?: string };
}

/** A single "how do I…" answer: the question, and every way to do it. */
export interface HowTo {
  id: string;
  /** The question as a newcomer would scan or type it. */
  q: string;
  /** Extra words people search by, so synonyms find the same answer. */
  keywords: string;
  /** Every genuine way to do it, best/most-common first. Never empty. */
  ways: HelpWay[];
  /** The job this belongs to, for the Help screen's grouped index. */
  group: string;
}

/**
 * The corpus. COMPLETE for the actions it covers, and accurate to the live app —
 * every way below was read off the component it points at (file + line in the
 * audit, 14 Oct 2026). Grouped by the job a newcomer is trying to do, in the
 * order they meet them.
 */
export const HOW_TO: HowTo[] = [
  // ——— Getting started ———
  {
    id: 'create-board',
    q: 'How do I start a project or board?',
    keywords: 'create new project board start set up begin continuous service',
    ways: [
      {
        via: 'From the switcher',
        steps:
          'Open the switcher at the top left → “New board” → choose “A project” or “A continuous service” → “Continue”. A project runs a short wizard (name it, phases, an optional charter) ending on “Create project”; a continuous service is one step, “Create & open board”.',
        target: { view: 'board' },
      },
    ],
    group: 'Getting started',
  },
  {
    id: 'find-anything',
    q: 'How do I find anything quickly?',
    keywords: 'find search jump go to open command palette shortcut quick cmd k ctrl anything navigate',
    ways: [
      {
        via: 'With the keyboard',
        steps:
          'Press ⌘K (Ctrl + K) anywhere → type a screen, a card title or a “#” number to jump straight to it, or type a few words of a question (“how do I…”) to search this help.',
        target: { view: 'board' },
      },
      {
        via: 'The ⌘K badge',
        steps:
          'On the Board, Backlog, Releases or Documents, click the “⌘K” badge at the right of the top search box to open the same palette.',
        target: { view: 'board' },
      },
    ],
    group: 'Getting started',
  },
  {
    id: 'get-help',
    q: 'How do I get help or learn the app?',
    keywords: 'help how do i guide stuck learn tips support what can i do get started teach',
    ways: [
      {
        via: 'The Help page',
        steps:
          'Click “Help” — the row under “Settings” at the foot of the sidebar — to open this page. Search, or browse by job; every answer has a “Take me there” that goes to the control and highlights it.',
        target: { view: 'help' },
      },
      {
        via: 'From ⌘K',
        steps: 'Press ⌘K and type a few words of a question; matching answers appear, and ↵ takes you to the control.',
        target: { view: 'board' },
      },
    ],
    group: 'Getting started',
  },
  {
    id: 'switch-workspace',
    q: 'How do I switch board or workspace?',
    keywords: 'switch change move workspace board company tenant switcher between organisation',
    ways: [
      {
        via: 'Switch board',
        steps: 'Open the switcher at the top left → under “Projects”, pick another board. It swaps instantly.',
        target: { view: 'board' },
      },
      {
        via: 'Switch workspace',
        steps:
          'Open the switcher → under “You are in N workspaces”, pick another company. Switching workspace reloads the app to that company’s data. (This section only shows if you are in more than one.)',
        target: { view: 'board' },
      },
    ],
    group: 'Getting started',
  },
  {
    id: 'notifications-bell',
    q: 'How do I see what needs my attention?',
    keywords: 'notifications bell alerts unread what needs me attention inbox mentions updates new my work',
    ways: [
      {
        via: 'The bell',
        steps:
          'Click the bell at the top right → it lists your call-outs across every board, newest first; opening the panel marks them read, and a row jumps straight to the card, sign-off or release it is about.',
        target: { view: 'board' },
      },
      {
        via: 'My work → Inbox',
        steps:
          'Open “My work” at the top of the sidebar → the “Inbox” tab gathers the same call-outs, split into “Needs you first” and “FYI”.',
        target: { view: 'mywork' },
      },
    ],
    group: 'Getting started',
  },
  {
    id: 'choose-cadence',
    q: 'How do I choose sprints or a flow board?',
    keywords: 'cadence sprints flow builds kanban iterations how plan mode board type continuous flow',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings (foot of the sidebar) → the “Workflow” tab → “How this board plans its work” → choose “Flow” (a continuous board) or “Sprints” (fixed-length). A “Sprints” screen appears only once Sprints is chosen; switching back hides sprints rather than deleting them.',
        target: { view: 'settings', reveal: 'cadence' },
      },
    ],
    group: 'Getting started',
  },
  {
    id: 'go-live-in-service',
    q: 'How do I mark a project live and move it to service?',
    keywords:
      'go live golive in service handover handoff launch deliver delivered continuous run bau business as usual mark live ship shipped went live as-delivered record settling in overdue',
    ways: [
      {
        via: 'On the board, when it is ready',
        steps:
          'When a project reaches its planned Go-Live and its delivery phases are done, a “ready to go into service” prompt appears on its board. It shows what gets frozen — the actual Go-Live (which you can edit), scope shipped vs deferred, and budget at Go-Live — then “Mark it Live · Move to Service” records the delivery in the board’s History and flips the board to a continuous service. You can mark it live early, or while a phase is still running (Hypercare), from the same prompt.',
        target: { view: 'board' },
      },
      {
        via: 'To move it back',
        steps:
          'Open Settings (foot of the sidebar) → the “Workflow” tab → “How this board is run”. A board in service shows “Move back to a project”, which clears the Go-Live and shows the Plan and Finances again. The as-delivered record is kept, so nothing about the delivery is lost.',
        target: { view: 'settings', reveal: 'delivery-mode' },
      },
    ],
    group: 'Getting started',
  },
  {
    id: 'rename-board',
    q: 'How do I rename a board?',
    keywords: 'rename board project name change title edit key',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Project” tab → “Project name” → type the new name. It saves as you type — there is no Save button. (The same panel holds the “Project key” used in issue labels.)',
        target: { view: 'settings', reveal: 'project-name' },
      },
    ],
    group: 'Getting started',
  },
  {
    id: 'archive-board',
    q: 'How do I archive or close a board?',
    keywords: 'archive close finish put away retire board done remove old restore bring back',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Releases & data” tab → “Putting this board away” → “Put <board> away” → confirm. Nothing is deleted, and you can bring it back from the same place on any other board. You can’t put your only board away.',
        target: { view: 'settings', reveal: 'archive' },
      },
    ],
    group: 'Getting started',
  },
  {
    id: 'my-work',
    q: 'How do I see all my work across every board?',
    keywords:
      'my work mywork assigned to me inbox across boards everything mine needs my action due overdue blocked by reason personal home to do',
    ways: [
      {
        via: 'The sidebar row',
        steps:
          '“My work” sits at the very top of the sidebar, on every board. The “To do” tab lays out your cards as “Needs you first”, “Doing now” and “Up next”, with “My epics” and “My areas” (or “Blocked, by reason”) alongside; the “Inbox” tab holds what has been sent to you. Reply, mark done or undo without leaving it.',
        target: { view: 'mywork' },
      },
      {
        via: 'Open here at sign-in',
        steps: 'Make it your landing screen: your account (avatar, top right) → Profile → “Open to” → “My work”.',
        target: { view: 'account', reveal: 'profile' },
      },
    ],
    group: 'Getting started',
  },

  // ——— Your account & sign-in ———
  {
    id: 'set-password',
    q: 'How do I set or change my password?',
    keywords: 'password passphrase sign in security set change reset login credentials link',
    ways: [
      {
        via: 'In your account',
        steps:
          'Open your account (avatar, top right) → “Sign-in and security” → type “New password” and “And again” → “Set password”.',
        target: { view: 'account', reveal: 'password' },
      },
      {
        via: 'Signed out',
        steps:
          'Signed out and can’t get in? On the sign-in screen, under “No password, or cannot remember it?”, enter your email and choose “Set a new password” — a link is emailed to you. (A password is optional; most people here sign in with a link.)',
      },
    ],
    group: 'Your account & sign-in',
  },
  {
    id: 'edit-profile',
    q: 'How do I change my name?',
    keywords: 'profile name display rename me my details account identity job title',
    ways: [
      {
        via: 'In your account',
        steps:
          'Open your account (avatar, top right) → “Profile” → edit “First name” and “Last name” (and “Job title” if you like); each saves when you click away.',
        target: { view: 'account', reveal: 'profile' },
      },
    ],
    group: 'Your account & sign-in',
  },
  {
    id: 'notification-prefs',
    q: 'How do I change what I am emailed about?',
    keywords: 'notifications email alerts preferences mute turn off on mentions sign-off cover away',
    ways: [
      {
        via: 'In your account',
        steps:
          'Open your account (avatar, top right) → “Notifications” → set “Tell me about” (every comment / only when mentioned / never), switch “Email me when I’m away”, and turn each kind on or off (mentions, replies, assignments, blocks, due dates, sign-offs, cover…).',
        target: { view: 'account', reveal: 'notify-prefs' },
      },
    ],
    group: 'Your account & sign-in',
  },
  {
    id: 'set-cover',
    q: 'How do I set cover while I am away?',
    keywords: 'away cover leave holiday delegate nominate out of office vacation absence sign-off while away',
    ways: [
      {
        via: 'In your account',
        steps:
          'Open your account (avatar, top right) → “When I’m away” → “Choose who covers for me” → pick a teammate. Set an “Until” date if you like, and switch cover on; they sign your sign-offs while you are away.',
        target: { view: 'account', reveal: 'cover' },
      },
      {
        via: 'An admin arranges it',
        steps:
          'An admin can set your cover for you: Workspace → People & access → your name → “Sign-off cover” (a reason is required).',
        target: { view: 'admin' },
      },
    ],
    group: 'Your account & sign-in',
  },
  {
    id: 'devices',
    q: 'How do I see where I am signed in?',
    keywords: 'devices sessions signed in sign out everywhere security laptop logout other browser',
    ways: [
      {
        via: 'In your account',
        steps:
          'Open your account (avatar, top right) → “Devices” → every browser you are signed in on; “Sign out” ends one, or “Sign out everywhere” ends them all (including this one).',
        target: { view: 'account', reveal: 'devices' },
      },
    ],
    group: 'Your account & sign-in',
  },
  {
    id: 'security-activity',
    q: 'How do I review my account activity?',
    keywords: 'security activity history sign-ins account log suspicious recent changes',
    ways: [
      {
        via: 'In your account',
        steps:
          'Open your account (avatar, top right) → “Security activity” → every sign-in and change on your account, newest first. Only you and an admin can see it.',
        target: { view: 'account', reveal: 'security-activity' },
      },
    ],
    group: 'Your account & sign-in',
  },
  {
    id: 'my-workspaces',
    q: 'How do I see the workspaces I am in?',
    keywords: 'workspaces memberships organisations my which belong switch list',
    ways: [
      {
        via: 'In your account',
        steps:
          'Open your account (avatar, top right) → “Workspaces” → every workspace you belong to, with your role in each. It is a read-only list.',
        target: { view: 'account', reveal: 'workspaces' },
      },
      {
        via: 'Switch from the sidebar',
        steps: 'To move to another workspace, use the switcher at the top of the sidebar — not this list.',
        target: { view: 'board' },
      },
    ],
    group: 'Your account & sign-in',
  },
  {
    id: 'sign-out',
    q: 'How do I sign out?',
    keywords: 'sign out log out leave exit logout everywhere',
    ways: [
      {
        via: 'From your account',
        steps: 'Open your account (avatar, top right) → “Profile” → “Sign out”.',
        target: { view: 'account', reveal: 'profile' },
      },
      {
        via: 'Everywhere at once',
        steps: 'Your account → “Devices” → “Sign out everywhere” ends every browser, including this one.',
        target: { view: 'account', reveal: 'devices' },
      },
      {
        via: 'With the palette',
        steps: 'Press ⌘K → type “Sign out” → ↵.',
        target: { view: 'board' },
      },
    ],
    group: 'Your account & sign-in',
  },

  // ——— The board & cards ———
  // Card FIELDS are edited after "Edit" (or press E) and saved with "Save
  // Changes" — the read view does not edit. Many fields can ALSO be set inline in
  // the quick-add box as you type the card. Both are given as ways.
  {
    id: 'add-card',
    q: 'How do I add a task or card?',
    keywords: 'add task card issue new create backlog quick add',
    ways: [
      {
        via: 'In the quick-add box',
        steps:
          'On the Board, type the task in the box at the top (“Add a card…”) and press Enter. Set details inline as you type: @who, #phase, !priority, *type, ~size and ^due (e.g. ^fri, ^+2w — no space after the ^).',
        target: { view: 'board', reveal: 'quick-add' },
      },
      {
        via: 'New issue',
        steps: 'Click “New issue” (in the sidebar or the top bar) → fill the title → “Create Issue”.',
        target: { view: 'board' },
      },
      {
        via: 'On the Plan',
        steps:
          'On a project’s Plan, in a phase click “+ Add a card to this phase, or pull one in” → type a title (with @who *type !priority ~size ^due) → Enter.',
        target: { view: 'plan' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'set-due-date',
    q: 'How do I set a due date?',
    keywords: 'due date deadline when schedule card',
    ways: [
      {
        via: 'In the open card',
        steps:
          'Open the card → “Edit” (or press E) → “Due date” (type “15 Sep”, “+2w”, “fri”, or pick from the calendar) → “Save Changes”.',
        target: { view: 'board' },
      },
      {
        via: 'In the quick-add box',
        steps:
          'When adding a card, type ^ and a date with no space: ^today, ^fri, ^+2w, ^eom or ^2026-10-20. A bare ^ opens a date menu.',
        target: { view: 'board', reveal: 'quick-add' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'set-start-date',
    q: 'How do I set a start date?',
    keywords: 'start date begin planned when scheduled kick off',
    ways: [
      {
        via: 'In the open card',
        steps:
          'Open the card → “Edit” → “Start date” → “Save Changes”. (The actual start is stamped separately when the card first moves to In Progress.)',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'assign-work',
    q: 'How do I assign work to someone?',
    keywords: 'assign owner assignee who responsible give delegate card',
    ways: [
      {
        via: 'In the open card',
        steps: 'Open the card → “Edit” → “Accountable” (and “Responsible” for the doer) → “Save Changes”.',
        target: { view: 'board' },
      },
      {
        via: 'In the quick-add box',
        steps: 'When adding a card, type @ and part of a name to assign it. With no @, it is assigned to you.',
        target: { view: 'board', reveal: 'quick-add' },
      },
      {
        via: 'With the palette',
        steps: 'With the card open (not editing), press ⌘K → “Assign to me” or “Unassign”.',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'set-size',
    q: 'How do I estimate or size a card?',
    keywords: 'size estimate points effort small large weight sizing quick long',
    ways: [
      {
        via: 'In the open card',
        steps: 'Open the card → “Edit” → “Size” (Quick / A few days / Long) → “Save Changes”.',
        target: { view: 'board' },
      },
      {
        via: 'In the quick-add box',
        steps: 'When adding a card, type ~ and a size: ~quick, ~medium or ~long.',
        target: { view: 'board', reveal: 'quick-add' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'set-priority',
    q: 'How do I set a card’s priority?',
    keywords: 'priority urgent importance high low critical rank order',
    ways: [
      {
        via: 'In the open card',
        steps: 'Open the card → “Edit” → “Priority” (Critical / High / Medium / Low) → “Save Changes”.',
        target: { view: 'board' },
      },
      {
        via: 'In the quick-add box',
        steps: 'When adding a card, type ! and a level: !critical, !high, !medium or !low.',
        target: { view: 'board', reveal: 'quick-add' },
      },
      {
        via: 'With the palette',
        steps: 'With the card open (not editing), press ⌘K → pick “Critical / High / Medium / Low”.',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'set-status',
    q: 'How do I move a card between columns?',
    keywords: 'status move column to do in progress done stage drag change',
    ways: [
      {
        via: 'By dragging',
        steps:
          'On the Board, drag the card to another column. Dropping on “Done” is refused until the board’s “done” checks are ticked.',
        target: { view: 'board' },
      },
      {
        via: 'In the open card',
        steps: 'Open the card → “Edit” → “Status” → “Save Changes”.',
        target: { view: 'board' },
      },
      {
        via: 'In the card feed',
        steps: 'Open the card → in “Updates & comments”, set the “Status:” beside “Post” and post it.',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'edit-card',
    q: 'How do I edit a card’s title or details?',
    keywords: 'edit card title description change update rename details text wording save',
    ways: [
      {
        via: 'In the open card',
        steps:
          'Open the card → “Edit” (or press E) → change the title, the description or any field → “Save Changes” (⌘ + Enter also saves). “Cancel” or Esc steps back, and asks before discarding changes.',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'set-type',
    q: 'How do I set the type of work on a card?',
    keywords: 'type kind feature bug fix improvement upkeep category means work',
    ways: [
      {
        via: 'In the open card',
        steps:
          'Open the card → “Edit” → “Type” → “Save Changes”. (The board’s types are set up in Settings → Workflow → “What sort of work a card is”.)',
        target: { view: 'board' },
      },
      {
        via: 'In the quick-add box',
        steps: 'When adding a card, type * and the type (e.g. *bug).',
        target: { view: 'board', reveal: 'quick-add' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'set-area',
    q: 'How do I set a card’s area?',
    keywords: 'area component part module system where section',
    ways: [
      {
        via: 'In the open card',
        steps: 'Open the card → “Edit” → “Area” → “Save Changes”.',
        target: { view: 'board' },
      },
      {
        via: 'Set the areas up first',
        steps:
          'Add the areas work can be tagged to in Settings → the “Workflow” tab → “The parts of this product” → “Add an area”.',
        target: { view: 'settings', reveal: 'classification' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'set-epic',
    q: 'How do I group cards under an epic?',
    keywords: 'epic group theme parent initiative feature group',
    ways: [
      {
        via: 'In the open card',
        steps:
          'Open the card → “Edit” → “Epic” (type a name; existing epics are suggested, and a new name creates one) → “Save Changes”.',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'mark-blocked',
    q: 'How do I mark a card as blocked?',
    keywords:
      'blocked blocker waiting on stuck held up dependency impediment flag awaiting feedback blocked by a card notify told named person link',
    ways: [
      {
        via: 'In the open card',
        steps:
          'Open the card → “Edit” → “What’s blocking this” → “Awaiting feedback from…” or “Blocked by a card…” → choose what it is waiting on and name a card or a person → “Add blocker” → “Save Changes”. A named card shows its live status and the block clears itself once every named card is Done; a named person is told in their feed that your card is waiting on them.',
        target: { view: 'board' },
      },
      {
        via: 'Clear one from Next up',
        steps: 'On the Board, “Next up” → “Resolve Blocker” clears a block without opening the card.',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'add-comment',
    q: 'How do I comment on a card or mention someone?',
    keywords: 'comment note discuss mention @ reply message update tag post',
    ways: [
      {
        via: 'In the card feed',
        steps:
          'Open the card → “Updates & comments” → write in the box, choose “Update” (notifies the people on this card) or “Note” (recorded quietly) → “Post”. Type @ to mention someone — they are notified either way.',
        target: { view: 'board' },
      },
      {
        via: 'Reply in the feed',
        steps: 'On any update or note, “Reply” to answer that thread.',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'group-board',
    q: 'How do I group or arrange the board?',
    keywords: 'group arrange organise swimlanes columns by phase epic area owner assignee lay out view',
    ways: [
      {
        via: 'On the board',
        steps:
          'On the Board, “Group by” → “Accountable”, “Phase”, “Epic” or “Area” lays the cards into swimlanes; “None” clears it. (To group by status, use the Backlog’s “Display”.)',
        target: { view: 'board', reveal: 'group-by' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'mark-done',
    q: 'How do I mark a card done?',
    keywords: 'done complete finish won’t do close drop finished mark',
    ways: [
      {
        via: 'In the open card',
        steps:
          'Open the card → “Edit” → “Status” → “Done” (or “Won’t Do” if it is being dropped) → “Save Changes”. If the board has “done” checks, tick them in “Before this can be done” first. The finish date is stamped automatically.',
        target: { view: 'board' },
      },
      {
        via: 'By dragging',
        steps: 'Drag the card to the “Done” column (refused until the “done” checks are ticked).',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'view-one-sprint',
    q: 'How do I show just one sprint on the board?',
    keywords:
      'sprint picker filter board one sprint only current running this sprint focus hide other sprints show all which sprint',
    ways: [
      {
        via: 'The sprint picker',
        steps:
          'The board has to plan in sprints (Settings → Workflow → “How this board plans its work” → “Sprints”) and have at least one sprint. Then on the Board open the sprint picker (the calendar button in the board tools) and choose a sprint to show only its cards. A banner names the sprint; “Show all sprints” clears it.',
        target: { view: 'board', reveal: 'sprint-pick' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'collapse-swimlane',
    q: 'How do I collapse a swimlane?',
    keywords: 'collapse fold swimlane lane expand hide group heading tidy shrink section collapse all expand all',
    ways: [
      {
        via: 'Fold a lane',
        steps:
          'Group the board into swimlanes first (“Group by”), then click a lane’s heading to fold it down to its header; click again to open it.',
        target: { view: 'board', reveal: 'group-by' },
      },
      {
        via: 'Collapse all',
        steps: 'While grouped, “Collapse all” at the end of the board tools folds every lane at once; it then reads “Expand all”.',
        target: { view: 'board', reveal: 'group-by' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'react-to-update',
    q: 'How do I react to an update or comment?',
    keywords: 'reaction emoji react like thumbs up acknowledge feed update comment card respond',
    ways: [
      {
        via: 'In the card feed',
        steps:
          'Open the card → under any update, note or reply, click the reaction icon (“Add a reaction”) and pick one. One per person — pick another to change yours, or click your own to clear it.',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'card-summary',
    q: 'How do I say in one line what a card is about?',
    keywords: 'summary what is this about plain english one line reader signer deliverable face description context',
    ways: [
      {
        via: 'In the open card',
        steps:
          'Open the card → “Edit” → “What is this about?” → “Save Changes”. It is the plain-English line a reader or signer sees — on the card face, in change requests and in deliverables sent for sign-off.',
        target: { view: 'board' },
      },
      {
        via: 'From the card face',
        steps: 'Hover a card with no summary and click “+ What’s this about?” to jump straight into it.',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'reach-offscreen-column',
    q: 'How do I reach a column that has scrolled off the edge of the board?',
    keywords: 'off screen column hidden edge fade marker scroll sideways done column drag to reveal five columns in staging',
    ways: [
      {
        via: 'The edge marker',
        steps:
          'When the optional In Staging column makes the board scroll sideways, a soft fade and a round marker with an arrow and a count appear at the right edge for the nearest hidden column — click it to scroll there (its name is in the tooltip).',
        target: { view: 'board' },
      },
      {
        via: 'Drag toward the edge',
        steps: 'While dragging a card, hold it near the edge and the board scrolls, so you can drop it on a column that was off-screen.',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },

  // ——— Backlog & sprints ———
  {
    id: 'work-backlog',
    q: 'How do I use the backlog?',
    keywords: 'backlog queue triage list unscheduled not started later views filter sort display group',
    ways: [
      {
        via: 'The Backlog',
        steps:
          'Open Backlog (under Work) → every card as one list. Switch “Views” (Open, My work, Blocked, Everything, or your saved ones), use “Filter” to narrow by field, and “Display” to group, sort and show counts. Set a card’s status here to move it onto the board.',
        target: { view: 'backlog' },
      },
    ],
    group: 'Backlog & sprints',
  },
  {
    id: 'save-view',
    q: 'How do I save a filtered view?',
    keywords: 'save view filter saved views custom list keep reuse',
    ways: [
      {
        via: 'On the Backlog',
        steps:
          'Open Backlog → set your filters with “Filter” → click the “+” at the end of the “Views” tabs (“Save these filters as a view”) → name it → “Save view”. Remove a saved one with the × beside it.',
        target: { view: 'backlog' },
      },
    ],
    group: 'Backlog & sprints',
  },
  {
    id: 'plan-sprint',
    q: 'How do I plan a sprint?',
    keywords: 'sprint plan new create iteration timebox cycle run add cards',
    ways: [
      {
        via: 'On the Sprints screen',
        steps:
          'Open Sprints (under Work; shown when the board plans in sprints) → “Plan a sprint” → set “Name”, “Starts” and “Ends” → “Plan it”. Then open it, “Add from the backlog”, and “Start it” when ready.',
        target: { view: 'sprints' },
      },
      {
        via: 'From a card',
        steps: 'Open a card → the Sprint row → “Add to a sprint…” to put it into one.',
        target: { view: 'board' },
      },
      {
        via: 'Turn sprints on first',
        steps:
          'No Sprints screen? Switch the board to sprints in Settings → the “Workflow” tab → “How this board plans its work”.',
        target: { view: 'settings', reveal: 'cadence' },
      },
    ],
    group: 'Backlog & sprints',
  },
  {
    id: 'finish-sprint',
    q: 'How do I finish a sprint?',
    keywords: 'finish sprint close end complete carry over leftover rollover release',
    ways: [
      {
        via: 'On the Sprints screen',
        steps:
          'Open Sprints (under Work) → “Finish it” on the running sprint. If any cards are unfinished, choose whether they carry into the next sprint or go back to the backlog. Afterwards you are offered “Record a release”.',
        target: { view: 'sprints' },
      },
    ],
    group: 'Backlog & sprints',
  },
  {
    id: 'burndown',
    q: 'How do I see how the sprint is going?',
    keywords: 'burndown chart progress sprint remaining velocity how much left capacity',
    ways: [
      {
        via: 'On the Sprints screen',
        steps:
          'Open Sprints (under Work) → a running or finished sprint shows its burndown in the header — work left against time left. A planned sprint shows a capacity line instead.',
        target: { view: 'sprints' },
      },
    ],
    group: 'Backlog & sprints',
  },

  // ——— Planning & budget ———
  {
    id: 'set-scope',
    q: 'How do I set the scope and stakeholders?',
    keywords: 'scope stakeholders sponsor in out about project details',
    ways: [
      {
        via: 'In Project details',
        steps:
          'Open Plan (under Work) → “Project details” → “Edit” → under “Scope” fill “In scope” and “Out of scope”, and add stakeholders with “+ Add stakeholder”. It autosaves.',
        target: { view: 'plan', reveal: 'about' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'plan-phases',
    q: 'How do I plan phases and a Go-Live date?',
    keywords: 'plan phases go live date timeline stages add phase target end',
    ways: [
      {
        via: 'Add a phase',
        steps:
          'Open Plan (under Work) → “+ Add a phase” for each stage (name it → “Add it”); open a phase’s “Edit” to set its “Target end”.',
        target: { view: 'plan', reveal: 'add-phase' },
      },
      {
        via: 'Set the Go-Live date',
        steps: 'On the Plan header, “+ Set Go-Live date” (then the pencil beside it to change it).',
        target: { view: 'plan' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'set-budget',
    q: 'How do I set a budget?',
    keywords: 'budget cost spend money capex opex finance forecast baseline contract value',
    ways: [
      {
        via: 'In Budget & payments',
        steps:
          'Open Budget & payments (under Finances) → “Edit budget” → set the “Contract value” and the “Baseline (approved budget)” → “Save budget”. Spend then tracks against it.',
        target: { view: 'budgetpay', reveal: 'contract-value' },
      },
      {
        via: 'From the Plan',
        steps: 'On the Plan, “+ Set budget” (or “Manage budget” once a budget exists) opens the same screen.',
        target: { view: 'plan', reveal: 'budget' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'manage-invoices',
    q: 'How do I record spend against the budget?',
    keywords: 'invoice spend commitment cost ledger capex opex payment money record actuals bill vendor',
    ways: [
      {
        via: 'In Budget & payments',
        steps:
          'Open Budget & payments (under Finances) → “+ Add a payment” → fill the amount, vendor, pool (Capex/Opex) and status → “Add payment”. Click a line to open it.',
        target: { view: 'budgetpay' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'record-vendor',
    q: 'How do I record the vendor or contract?',
    keywords: 'vendor supplier contractor contract sow who delivers third party commercial',
    ways: [
      {
        via: 'In Project details',
        steps:
          'Open Plan (under Work) → “Project details” → “Edit” → under “Commercial” set “Vendor”, “Vendor contact” and “Contract / SOW reference”.',
        target: { view: 'plan', reveal: 'about' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'log-decision',
    q: 'How do I log a decision?',
    keywords: 'decision log record decided choice call governance adr rationale why who decided superseded open',
    ways: [
      {
        via: 'In the Decisions log',
        steps:
          'Open Decisions (under Work) → “Log a decision” → write the call and why, choose “Decided” or “Open” → “Log decision”. A decided decision is never edited — supersede it with a new one.',
        target: { view: 'decisions' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'auto-log-decision',
    q: 'How does a decision get logged automatically?',
    keywords:
      'decision automatic auto log capture derive without form blocker sign-off signoff change request assumption one click record where from itself seed how does it work',
    ways: [
      {
        via: 'On a settled sign-off',
        steps:
          'Open Sign-offs (under Deliver) → show the settled items → on a settled one, “Log as a decision” files it to the Decisions log, carrying the sign-off’s reason.',
        target: { view: 'approvals' },
      },
      {
        via: 'On a change-request assumption',
        steps:
          'Open the Change request document → on an assumption that is no longer “To be confirmed”, “Log as a decision” files it, linked back to the request.',
        target: { view: 'documents' },
      },
      {
        via: 'On a blocked card',
        steps:
          'A card blocked on a decision shows “What was decided?” — fill it and “Clear blocker & log decision” records the decision and lifts the block in one go.',
        target: { view: 'board' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'revise-decision',
    q: 'How do I change or revise a decision?',
    keywords:
      'change revise edit update decision supersede replace reverse overturn correct reopen amend withdraw new decision adr decided',
    ways: [
      {
        via: 'Supersede a decided one',
        steps:
          'Open Decisions (under Work) → the decision → “Supersede” → write the new call. The old one is kept, marked “Superseded” and linked to the one that replaced it.',
        target: { view: 'decisions' },
      },
      {
        via: 'Edit an open one',
        steps: 'A decision still marked “Open” can be edited in place with “Edit” until it is decided.',
        target: { view: 'decisions' },
      },
      {
        via: 'Withdraw one',
        steps: '“Withdraw” drops a decision from the log (a reason is required); it stays on the record.',
        target: { view: 'decisions' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'add-lesson',
    q: 'How do I capture a lesson learned?',
    keywords: 'lesson learned lessons retro retrospective what worked what didnt recommendation insight carry forward category',
    ways: [
      {
        via: 'In the Lessons register',
        steps:
          'Open Lessons (under Work) → “Add a lesson” → pick “What worked” or “What didn’t”, set the “Category” (Scope, Schedule, Cost, Risk…), write a “Title” and “What happened”, and optionally add a “Recommendation” → “Add lesson”.',
        target: { view: 'lessons' },
      },
      {
        via: 'From an after-action report',
        steps:
          'In an after-action report, on a line under “What to sustain and improve”, “Save as a lesson” files it to this project, pre-filled.',
        target: { view: 'documents' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'reuse-lessons',
    q: 'How do I reuse lessons from past projects?',
    keywords: 'lessons library reuse past projects workspace cross board kickoff filter category find learning carry in',
    ways: [
      {
        via: 'The Lessons library',
        steps:
          'Open Lessons library (under Team) → filter by kind and category to find the lessons that apply before you start. Each one shows the project it came from.',
        target: { view: 'lessonslibrary' },
      },
      {
        via: 'On a new project’s Plan',
        steps:
          'A young project’s Plan shows “Lessons worth carrying in” — recommendations from past projects, same type first.',
        target: { view: 'plan' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'file-project-lesson',
    q: 'How do I file a lesson to the right project?',
    keywords: 'lesson project tag file which board specific project belongs attach assign category workspace wide',
    ways: [
      {
        via: 'On that project’s board',
        steps:
          'A lesson is filed to the board you add it on — there is no project to pick, so open that project first, then Lessons (under Work) → “Add a lesson”. Set its “Category” so the Lessons library can find it across projects (the category is a tag; the board is what files it).',
        target: { view: 'lessons' },
      },
      {
        via: 'A workspace-wide lesson',
        steps:
          'In the Lessons library (under Team), “Add a workspace lesson” files one against no project — it belongs to the whole workspace.',
        target: { view: 'lessonslibrary' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'log-risk',
    q: 'How do I log a risk?',
    keywords:
      'risk register raid log add capture record likelihood impact severity owner mitigation response threat exposure',
    ways: [
      {
        via: 'In the risk register',
        steps:
          'Open Risk register (under Plan) → the Risks tab → “Add a risk” → write it, set “Likelihood” and “Impact” (the severity is worked out for you), name an “Owner” and the “Response” → “Add risk”. Risks are listed worst first.',
        target: { view: 'riskregister' },
      },
      {
        via: 'Seed it from a lesson',
        steps:
          'Open Lessons (under Work) → on a lesson, “Seed a risk” opens a risk pre-filled from it; check the detail and “Add to register”.',
        target: { view: 'lessons' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'risk-severity',
    q: 'How is a risk’s severity worked out?',
    keywords:
      'risk severity score likelihood impact 5x5 matrix band low medium high critical rating calculate heat map to assess exposure',
    ways: [
      {
        via: 'It is Likelihood × Impact',
        steps:
          'In the Risk register (under Plan), on the Risks tab, a risk’s severity is its “Likelihood” times its “Impact” on a 5×5 scale, banded Low, Medium, High or Critical — set both and the band shows itself. A risk still missing either reads “to assess”, never a false Low.',
        target: { view: 'riskregister' },
      },
      {
        via: 'See them on the matrix',
        steps:
          'On the Risks tab, switch “Table” to “Matrix” for the 5×5 heat map — every risk sits where its likelihood meets its impact, worst in the top corner.',
        target: { view: 'riskregister' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'raid-register',
    q: 'How do I track assumptions, issues and dependencies?',
    keywords:
      'raid assumption issue dependency register track log add tab confidence blocked waiting on overdue needed by direction register',
    ways: [
      {
        via: 'The four RAID tabs',
        steps:
          'Open Risk register (under Plan) → the tab strip carries Risks, Assumptions, Issues and Dependencies, each with its own columns and at-a-glance read. Use “Add an assumption”, “Add an issue” or “Add a dependency” on the tab you want.',
        target: { view: 'riskregister' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'raid-convert',
    q: 'How do I turn an assumption into a risk, or a risk into an issue?',
    keywords:
      'convert assumption risk issue realise realised promote escalate raise becomes happened invalidated no longer holds raid link',
    ways: [
      {
        via: 'An assumption that no longer holds',
        steps:
          'In the Risk register → the Assumptions tab → on the assumption, “Convert to risk” opens a risk carried over from it.',
        target: { view: 'riskregister' },
      },
      {
        via: 'A risk that has happened',
        steps:
          'In the Risk register → the Risks tab → on the risk, “Realise as an issue” raises it on the Issues tab, marked “Realised from” that risk.',
        target: { view: 'riskregister' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'charter-risks-from-register',
    q: 'Where do my project’s risks show on the charter?',
    keywords:
      'charter risk register section render single source produce pdf document risks show appear sign off where',
    ways: [
      {
        via: 'The charter reads the register',
        steps:
          'The charter’s “Risk register” section is drawn from this register when you produce the charter PDF — add and tend risks in the Risk register (under Plan), not in the charter itself, so the charter stays accurate on its own.',
        target: { view: 'riskregister' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'paid-date',
    q: 'How do I record the date a payment actually cleared?',
    keywords: 'paid date payment date value date reconcile finance cleared mark paid correct when paid ledger settled',
    ways: [
      {
        via: 'In Budget & payments',
        steps:
          'Open Budget & payments (under Finances) → open the line. Once it is paid (“Mark paid”), under “Invoice details” use “Set the finance date” to enter the real “Payment date” from your finance system. The in-app “marked paid” moment is kept too; correcting it later asks “Why the change?”.',
        target: { view: 'budgetpay' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'payment-waiting-on',
    q: 'How do I see who a payment is waiting on?',
    keywords: 'authorisation chain who approves payment waiting stage quorum signatures outstanding approver authorise invoice',
    ways: [
      {
        via: 'In the invoice line',
        steps:
          'Open Budget & payments (under Finances) → open the line → the “Authorisation chain” section names the open stage and how many signatures it still needs; “Authorise payment” if it is yours. If it says “No payment approver is set”, set the chain up first.',
        target: { view: 'budgetpay' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'set-payment-approvers',
    q: 'How do I set who approves payments?',
    keywords:
      'payment approval chain approvers stages quorum value band threshold who authorises executive sign payment setup admin finance',
    ways: [
      {
        via: 'The Payment approval screen',
        steps:
          'Open Payment approval (under Finances; admins only) → “Add a stage” for each approval step (cleared by any one signer, a set number — a quorum — or all of them) → add a “value band” so a higher signer joins above a threshold. A project can follow the default or set its own stages.',
        target: { view: 'payapproval' },
      },
      {
        via: 'A single approver on a project',
        steps:
          'For one named approver on this project instead, Plan → “Project details” → “Payment approvals” → “Set an approver”.',
        target: { view: 'plan', reveal: 'pay-approvers' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'reclassify-line',
    q: 'How do I change a payment’s cost type or what it’s billed against?',
    keywords: 'classify reclassify capex opex phase milestone billed against delivery tracked correction reason cost type',
    ways: [
      {
        via: 'In the invoice line',
        steps:
          'Open Budget & payments (under Finances) → open the line → the “Classification” section sets its Capex/Opex, phase and what it is billed against. On a paid line, “Reclassify” asks for the new values and a reason (paid lines go for approval). The ⋯ menu on a ledger row has “Reclassify” too.',
        target: { view: 'budgetpay' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'billable-milestones',
    q: 'How do I add milestones and billable payment stages?',
    keywords: 'milestone billable bill value payment stage schedule of values dated point claim invoice against milestone contract',
    ways: [
      {
        via: 'On the Plan',
        steps:
          'Open Plan (under Work) → the “Milestones” card → “Add milestone” (name, type, target date). Turn on “Billable” and set a “Bill value” to make it a payment stage, reconciled to the contract; each row reads Scheduled / Claimed to date / Paid / Remaining, and you raise the claim in Budget & payments.',
        target: { view: 'plan', reveal: 'billable' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'contract-value',
    q: 'How do I set or change the contract value?',
    keywords: 'contract value scope change adjust baseline variation agreed total sow budget from contract record scope governance',
    ways: [
      {
        via: 'In Budget & payments',
        steps:
          'Open Budget & payments (under Finances) → “Edit budget” → set the “Contract value”; the budget baseline follows it. Once it is set, changing it opens “Adjust the contract value” — a reason is required and it is recorded as a scope change on the governance trail.',
        target: { view: 'budgetpay', reveal: 'contract-value' },
      },
      {
        via: 'Who may change it',
        steps:
          'Settings → the “Finance & payments” tab → “Require a separate approver for scope changes” decides who is allowed to adjust it.',
        target: { view: 'settings', reveal: 'scope-approver' },
      },
    ],
    group: 'Planning & budget',
  },

  // ——— Documents & sign-off ———
  {
    id: 'write-charter',
    q: 'How do I write the project charter?',
    keywords: 'charter mandate sponsor objective scope start document purpose objectives',
    ways: [
      {
        via: 'New document',
        steps:
          'Open Documents (under Deliver) → “New document” → “Project charter” → “Create it”, then fill its sections (Purpose, Objectives, Milestones, Risk register, RACI, Governance). The sponsor, scope and assumptions are pulled from Plan → Project details.',
        target: { view: 'documents', reveal: 'new-document' },
      },
      {
        via: 'From the Plan',
        steps: 'Open Plan (under Work) → “Project details” → “Start a charter” drafts it and opens it.',
        target: { view: 'plan', reveal: 'about' },
      },
    ],
    group: 'Documents & sign-off',
  },
  {
    id: 'charter-milestones',
    q: 'How do I record milestones, risks or RACI?',
    keywords:
      'milestones risks raci assumptions constraints charter narrative dates responsibility accountable consulted informed scope',
    ways: [
      {
        via: 'In the charter',
        steps:
          'Milestones, risks and RACI are tables inside the charter. Open the charter (Plan → “Open the charter”, or Documents → the charter row) → fill the “Milestones”, “Risk register” and “Roles & responsibilities (RACI)” tables; they autosave.',
        target: { view: 'documents' },
      },
      {
        via: 'In Project details',
        steps:
          'Scope, assumptions and constraints are not typed in the charter — set them in Plan → “Project details” → “Edit” (“In scope”, “Out of scope”, “Assumptions”, “Constraints”). The charter weaves them in when it is produced.',
        target: { view: 'plan', reveal: 'about' },
      },
    ],
    group: 'Documents & sign-off',
  },
  {
    id: 'raise-change-request',
    q: 'How do I raise a change request?',
    keywords: 'change request cr scope change document raise new sprint',
    ways: [
      {
        via: 'New document',
        steps:
          'Open Documents (under Deliver) → “New document” → “Change request” → title it and choose what it covers (the whole project or a sprint) → “Create it”. The item lines are built from the cards; you write the narrative and the “Assumptions & exceptions”, then “Produce the PDF”.',
        target: { view: 'documents', reveal: 'new-document' },
      },
      {
        via: 'From a sprint',
        steps:
          'On a sprint that uses the sign-off model, Sprints → open the sprint → the “Sign-off” panel → “Create the change request”.',
        target: { view: 'sprints' },
      },
    ],
    group: 'Documents & sign-off',
  },
  {
    id: 'upload-received',
    q: 'How do I keep a contract or reference file?',
    keywords: 'upload received reference contract sow statement of work attach file keep document add existing pdf excel',
    ways: [
      {
        via: 'New document',
        steps:
          'Open Documents (under Deliver) → “New document” → under “Documents you receive” pick “Statement of work”, “Project plan”, “Acceptance” or “Attachment” → “Create it” → “Choose the document” (PDF, Word, Excel, CSV or image, up to 25 MB). Received files are kept and read, never signed.',
        target: { view: 'documents', reveal: 'new-document' },
      },
    ],
    group: 'Documents & sign-off',
  },
  {
    id: 'produce-pdf',
    q: 'How do I produce a document as a PDF?',
    keywords: 'produce pdf generate export document make render print signable',
    ways: [
      {
        via: 'In the document',
        steps:
          'Open Documents (under Deliver) → open a draft you have written (charter, change request, requirements, after-action report…) → “Produce the PDF” to generate it from what you entered (it becomes “Produce it again” afterwards). Producing it is what makes it signable.',
        target: { view: 'documents' },
      },
    ],
    group: 'Documents & sign-off',
  },
  {
    id: 'get-signoff',
    q: 'How do I get a deliverable signed off?',
    keywords: 'sign off signoff approve approval signature route send document approvers',
    ways: [
      {
        via: 'Send for sign-off',
        steps:
          'Open Documents (under Deliver) → open the document → produce the PDF (or upload the file) → “Send for sign-off”. The people named in Settings → Sign-off are copied onto it and cannot be changed afterwards.',
        target: { view: 'documents' },
      },
      {
        via: 'Set who signs first',
        steps: 'Settings → the “Sign-off” tab sets who approves this board’s work.',
        target: { view: 'settings', reveal: 'signoff' },
      },
    ],
    group: 'Documents & sign-off',
  },
  {
    id: 'sign-waiting',
    q: 'How do I sign off something waiting for me?',
    keywords: 'sign off signoff approve decline reject waiting my outstanding signature review inbox release sprint document',
    ways: [
      {
        via: 'On the Sign-offs screen',
        steps:
          'Open Sign-offs (under Deliver) → the tabs list everything outstanding, flagged “Sign” where it needs you → “Sign off” opens it, and you record “Approved” or “Rejected” on your row.',
        target: { view: 'approvals' },
      },
      {
        via: 'From My work',
        steps: 'My work → “Needs you first” → “Sign” jumps you to the sign-off.',
        target: { view: 'mywork' },
      },
      {
        via: 'From an emailed link',
        steps: 'A signatory without an account signs from the link emailed to them — no sign-in needed.',
        target: { view: 'approvals' },
      },
    ],
    group: 'Documents & sign-off',
  },
  {
    id: 'record-of-approval',
    q: 'How do I see who signed a document?',
    keywords: 'record of approval who signed proof signatures approved certificate when download copy',
    ways: [
      {
        via: 'Download the signed copy',
        steps:
          'Open Documents (under Deliver) → open a signed document → “Download approved copy”. The PDF carries a “Record of approval” page — who signed and when.',
        target: { view: 'documents' },
      },
      {
        via: 'On screen',
        steps:
          'The open document’s signature table shows each signatory’s “Approved” or “Rejected”, with the date and any reason, and a “Signed off & locked” note.',
        target: { view: 'documents' },
      },
    ],
    group: 'Documents & sign-off',
  },
  {
    id: 'after-action-report',
    q: 'How do I produce the after-action report?',
    keywords: 'after action report aar closure retrospective closing lessons benefits variance project close assembled review',
    ways: [
      {
        via: 'New document',
        steps:
          'Open Documents (under Deliver) → “New document” → “After-action report” → “Create it”. It assembles itself from the board — what was set out versus delivered, benefits, the schedule and cost variance with the reason behind each, and the lessons. A line can be saved back as a lesson; then “Produce the PDF”.',
        target: { view: 'documents', reveal: 'new-document' },
      },
    ],
    group: 'Documents & sign-off',
  },

  // ——— Releases ———
  {
    id: 'cut-release',
    q: 'How do I record a release?',
    keywords: 'release build ship version cut new record notes',
    ways: [
      {
        via: 'Cut a release',
        steps:
          'Open Releases (under Deliver) → “Cut a release” → set the version, the date it went out, tick “What went out”, add a one-line summary → “Record <version>”.',
        target: { view: 'releases', reveal: 'cut-release' },
      },
      {
        via: 'After finishing a sprint',
        steps: 'When you finish a sprint, “Record a release” opens the same panel, narrowed to that sprint.',
        target: { view: 'sprints' },
      },
    ],
    group: 'Releases',
  },
  {
    id: 'release-notes',
    q: 'How do I set what a release says shipped?',
    keywords: 'release notes what shipped changelog version contents items one sentence',
    ways: [
      {
        via: 'When you cut it',
        steps:
          'A release’s one-line note and its card list are set when you cut it — the “In one sentence” field and “What went out” (tick the cards). They are part of recording the release rather than edited afterwards; to change what shipped, record a new release.',
        target: { view: 'releases', reveal: 'cut-release' },
      },
    ],
    group: 'Releases',
  },
  {
    id: 'sign-release',
    q: 'How do I sign off a release?',
    keywords: 'sign release approve awaiting sign-off signoff cut go live',
    ways: [
      {
        via: 'On the release',
        steps:
          'Open Releases (under Deliver) → a release awaiting approval shows “Awaiting sign-off”; “Ask for sign-off”, then signatories record “Approved” or “Rejected”, and a manager can “Cut the release” and “Mark as gone to live”.',
        target: { view: 'releases' },
      },
      {
        via: 'On the Sign-offs screen',
        steps: 'Or find it in Sign-offs (under Deliver) → the “Releases” tab → “Sign off”.',
        target: { view: 'approvals' },
      },
    ],
    group: 'Releases',
  },

  // ——— Reporting & insights ———
  {
    id: 'portfolio-status',
    q: 'How do I see where my projects stand?',
    keywords: 'portfolio status overview health across projects weekly meeting blocked waiting on',
    ways: [
      {
        via: 'The Portfolio',
        steps:
          'Open Portfolio (under Team; managers and above) → the “Projects” lens shows each project’s owner, health, progress, what it is waiting on, next milestone and budget. Click a row for its status report, or the chevron at the left of a row to expand its cards inline.',
        target: { view: 'portfolio' },
      },
    ],
    group: 'Reporting & insights',
  },
  {
    id: 'steering-pack',
    q: 'How do I produce the steering pack?',
    keywords: 'steering pack portfolio pack pdf report monthly committee download board pack produce',
    ways: [
      {
        via: 'Produce the pack',
        steps:
          'Open Portfolio (under Team) → the “Projects” lens → “Portfolio Report (PDF)” downloads the whole portfolio as a branded steering-committee pack.',
        target: { view: 'portfolio', reveal: 'produce-pack' },
      },
    ],
    group: 'Reporting & insights',
  },
  {
    id: 'read-insights',
    q: 'How do I read the Insights dashboard?',
    keywords: 'insights dashboard metrics flow throughput cycle time lead time ageing charts stats overview numbers',
    ways: [
      {
        via: 'Insights',
        steps:
          'Open Insights (under Team) → flow metrics for this board or all your boards — ageing work, throughput, cycle and lead time. “Arrange” → “Show panels” adds more (blockers, forecast finish…).',
        target: { view: 'dashboard' },
      },
    ],
    group: 'Reporting & insights',
  },
  {
    id: 'see-activity',
    q: 'How do I see what changed?',
    keywords: 'activity history log changes audit trail what happened recent timeline who did',
    ways: [
      {
        via: 'Activity',
        steps:
          'Open Activity (under Team) → this board’s running history, newest first; a period picker narrows it, and clicking a card entry opens it. (The workspace-wide record is Workspace → People & access → History.)',
        target: { view: 'activity' },
      },
    ],
    group: 'Reporting & insights',
  },
  {
    id: 'authorised-to-pay',
    q: 'How do I see all approved payments waiting to be paid?',
    keywords: 'ready to pay authorised to pay finance queue approved not paid export invoices across boards pay list total cross board',
    ways: [
      {
        via: 'Ready to Pay',
        steps:
          'Open “Ready to Pay” (under Finances; managers and above) → a read-only list of every board’s approved-but-unpaid invoices with the proof of authority, a “Total authorised” figure, and “Export for finance”.',
        target: { view: 'payments' },
      },
    ],
    group: 'Reporting & insights',
  },
  {
    id: 'portfolio-lenses',
    q: 'How do I see an epic or area across all boards?',
    keywords: 'epic across boards portfolio lens by epic by area milestones people pipeline in delivery in service roll up cross board',
    ways: [
      {
        via: 'The lens tabs',
        steps:
          'Open Portfolio (under Team) → the lens tabs at the top: “Projects”, “People”, “Milestones”, “Epics”, “Areas”. The Epics lens rolls every board’s cards into each epic, banded Pipeline / In delivery / In service; open one for its detail.',
        target: { view: 'portfolio' },
      },
    ],
    group: 'Reporting & insights',
  },

  // ——— People & workspace ———
  {
    id: 'invite-people',
    q: 'How do I add or invite people?',
    keywords: 'invite add people person team member colleague access email join',
    ways: [
      {
        via: 'Add someone (admins)',
        steps:
          'Open Workspace (under Team) → “People & access” → “People” → “Add someone” → fill a name, email, a role and which boards → “Add them”. They arrive by an emailed sign-in link.',
        target: { view: 'admin' },
      },
      {
        via: 'Ask an admin',
        steps:
          'If you are not an admin, Workspace → People & access → “Ask an admin to add someone” sends the request for you.',
        target: { view: 'admin' },
      },
    ],
    group: 'People & workspace',
  },
  {
    id: 'approve-join',
    q: 'How do I approve someone who asked to join?',
    keywords: 'approve decline request join access admin grant pending who asked requests',
    ways: [
      {
        via: 'The Requests tab',
        steps:
          'Open Workspace (under Team) → “People & access” → “Requests” (admins only) → set the role and “Approve & add”, or “Decline…” with a short reason.',
        target: { view: 'admin' },
      },
    ],
    group: 'People & workspace',
  },
  {
    id: 'set-roles',
    q: 'How do I change what someone can do?',
    keywords: 'roles permissions access manager reviewer member admin rights who can promote change role manage',
    ways: [
      {
        via: 'On a person',
        steps:
          'Open Workspace (under Team) → “People & access” → “People” → “Manage” on the person → set their “Default role”, switch on “Workspace admin”, or give a different role per board under “Boards they can open”.',
        target: { view: 'admin' },
      },
      {
        via: 'What each role can do',
        steps: 'The “Roles” tab explains what every role can and cannot do — a read-only reference.',
        target: { view: 'admin' },
      },
    ],
    group: 'People & workspace',
  },
  {
    id: 'teams-groups',
    q: 'How do I give several people access at once?',
    keywords: 'teams groups group access cohort department grant together board access per person',
    ways: [
      {
        via: 'Person by person (for now)',
        steps:
          'Grouping people into a team is not switched on in your workspace yet. For now, give board access person by person: Workspace (under Team) → “People & access” → “People” → “Manage” → “Boards they can open”.',
        target: { view: 'admin' },
      },
    ],
    group: 'People & workspace',
  },
  {
    id: 'workspace-history',
    q: 'How do I see the workspace’s history?',
    keywords: 'history audit workspace log who changed admin trail record security',
    ways: [
      {
        via: 'The History tab',
        steps:
          'Open Workspace (under Team) → “People & access” → “History” (admins only) → who did what, and when; filter it and “Download as a file”. Most entries are written by the system and cannot be changed.',
        target: { view: 'admin' },
      },
    ],
    group: 'People & workspace',
  },
  {
    id: 'set-currency',
    q: 'How do I set the currency or exchange rates?',
    keywords: 'currency exchange rate fx money base reporting convert forex rates',
    ways: [
      {
        via: 'In Workspace settings',
        steps:
          'Open Workspace (under Team) → “Settings” → “Currency & rates” → set the “Base currency” and an exchange rate for each other currency you pay in (admins only).',
        target: { view: 'admin' },
      },
      {
        via: 'From a board',
        steps:
          'Settings → the “Finance & payments” tab → “Currency & exchange rates” is the same, workspace-wide, editor.',
        target: { view: 'settings', reveal: 'currency' },
      },
    ],
    group: 'People & workspace',
  },
  {
    id: 'rename-workspace',
    q: 'How do I rename the workspace?',
    keywords: 'rename workspace name company organisation change title operator support',
    ways: [
      {
        via: 'Set by the operator',
        steps:
          'The workspace name (Workspace → “Settings” → “Workspace name”) is set by the platform operator, not inside the app — ask support to change it.',
        target: { view: 'admin' },
      },
    ],
    group: 'People & workspace',
  },

  // ——— Board setup ———
  {
    id: 'import-spreadsheet',
    q: 'How do I import tasks from a spreadsheet?',
    keywords: 'import spreadsheet excel csv upload bring in existing backlog migrate xlsx file',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Project” tab → “Bring a backlog in from a spreadsheet” → “Choose a file…” (Excel or CSV) → map the columns and check it; nothing is added until “Import N cards”.',
        target: { view: 'settings', reveal: 'import' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'define-areas',
    q: 'How do I set up the board’s areas?',
    keywords: 'areas components parts set up define board areas modules sections classified colour color',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Workflow” tab → “The parts of this product” → “Add an area” (name and colour) → “Add it”. A colour shows as the dot on cards and colours the lane when you group the board by area. Areas can be reordered, and Remove retires one.',
        target: { view: 'settings', reveal: 'classification' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'define-labels',
    q: 'How do I set up and colour the board’s labels?',
    keywords: 'labels tags set up define colour color chip cross-cutting retire restore manage board labels',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Workflow” tab → “Labels” → “Add a label”, give it a name and (optionally) a colour → “Add it”. The colour shows as the dot beside the label on a card. A label typed straight onto a card still works; adding it here is what gives it a colour and keeps track of it. Remove retires a label.',
        target: { view: 'settings', reveal: 'classification' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'define-types',
    q: 'How do I set up the kinds of work?',
    keywords: 'types issue kinds categories set up define feature bug upkeep work type classified',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Workflow” tab → “What sort of work a card is” → add a type and what it “Counts as” → “Add it”.',
        target: { view: 'settings', reveal: 'classification' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'define-done',
    q: 'How do I set what "done" means?',
    keywords: 'definition of done checks complete criteria quality gate done means finished',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Workflow” tab → “What “done” means here” → list the checks, one per line. Nothing can move to Done until every check is ticked.',
        target: { view: 'settings', reveal: 'done-checks' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'setup-signoff',
    q: 'How do I set who approves this board’s work?',
    keywords: 'sign-off setup approvers who approves who signs off set up sign off signatories panel configure signoff owner sponsor stages',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Sign-off” tab → set the project owner and who signs — named by hand, or as ordered stages with a rule by work type.',
        target: { view: 'settings', reveal: 'signoff' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'add-in-staging',
    q: 'How do I add an In Staging column?',
    keywords: 'in staging column deploy test qa pre-prod preprod environment extra fifth column between in progress in review add workflow lane',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Workflow” tab → “An In Staging column” → switch on “Add an In Staging column”; the board then shows In Staging between In Progress and In Review.',
        target: { view: 'settings', reveal: 'in-staging' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'hide-done-column',
    q: 'How do I hide the Done column?',
    keywords: 'hide done column remove finished off fewer columns clean board less scroll gaps empty',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Workflow” tab → with In Staging on, switch on “Hide the Done column while In Staging is on”. Cards still move to Done from a card’s own picker, and Done comes back when In Staging is off.',
        target: { view: 'settings', reveal: 'in-staging' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'delivery-mode',
    q: 'How do I run a board as a service instead of a project?',
    keywords: 'delivery mode continuous service project run bau ongoing operational no plan budget board type bounded',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Workflow” tab → “How this board is run” → choose “A project” or “A continuous service”. A continuous service hides the Plan and the Finances, since it has no end date or charter.',
        target: { view: 'settings', reveal: 'delivery-mode' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'separation-of-duties',
    q: 'How do I require a separate person to approve scope or payments?',
    keywords: 'separation of duties sod scope approver segregation control who can approve pay separate approver conflict',
    ways: [
      {
        via: 'Separation of duties',
        steps:
          'Open Settings → the “Finance & payments” tab → “Separation of duties” is always on: whoever accepts work cannot authorise its payment (an admin can override in an emergency, with a reason).',
        target: { view: 'settings', reveal: 'sod' },
      },
      {
        via: 'A separate scope approver',
        steps:
          'Just below, switch on “Require a separate approver for scope changes” so only the named approver can change the contract value.',
        target: { view: 'settings', reveal: 'scope-approver' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'delivery-nudge',
    q: 'How do I warn when a payment is approved before the work is delivered?',
    keywords: 'delivery nudge approve ahead of delivery warning percent delivered gate invoice before done soft gate',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Finance & payments” tab → “Approving ahead of delivery” → switch on “Warn when a payment is approved ahead of delivery” and set the “Warn below N%” bar. It warns and records the shortfall on the line — a soft gate, never a hard block.',
        target: { view: 'settings', reveal: 'delivery-nudge' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'board-logo',
    q: 'How do I put a logo on the documents this board produces?',
    keywords: 'logo branding letterhead client mark image upload document pdf customer brand png jpeg',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Project” tab → “Branding” → “Upload a logo” (PNG or JPEG, under 2MB). It is held per board and prints on the documents this board produces; “Replace it” swaps it, and you can remove it to go back to unbranded.',
        target: { view: 'settings', reveal: 'branding' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'issue-numbering',
    q: 'How do I change how issues are numbered?',
    keywords: 'numbering issue number key prefix plain numbers project key label id card reference how numbered',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Project” tab → “How issues are numbered” → choose “Plain numbers” or “Project key” (e.g. PO-142). It re-labels every card, in search and anywhere an issue is mentioned; the number itself never changes.',
        target: { view: 'settings', reveal: 'numbering' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'issue-source',
    q: 'How do I set where this project’s issues come from?',
    keywords: 'source where issues come from board origin raised captured label what to call it external tracker',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Project” tab → “Where this project’s issues come from” → choose “This board” or “An external tracker”, and name it. The name shows wherever the board explains where something came from.',
        target: { view: 'settings', reveal: 'source' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'ships-to-devices',
    q: 'How do I say this project ships to devices?',
    keywords: 'ships to devices installed hardware release device website hosted service deploy',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Releases & data” tab → “Versions and releases” → turn on “This project ships to devices” if releases are installed on hardware. Leave it off for a website or a hosted service, which is released rather than installed.',
        target: { view: 'settings', reveal: 'ships-to-devices' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'version-source',
    q: 'How do I control the version numbers on releases?',
    keywords: 'version numbering releases assistant build number who numbers automatic type version next',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Releases & data” tab → “Versions and releases” → leave “Versions come from the assistant” off to type the version yourself when you record a release (FlightDeck suggests the next one); turn it on if a tool or an assistant already numbers your builds.',
        target: { view: 'settings', reveal: 'version-numbering' },
      },
    ],
    group: 'Board setup',
  },

  // ——— Further actions the app offers (added 15 Oct 2026, from the full audit) ———
  {
    id: 'appearance',
    q: 'How do I switch between light and dark?',
    keywords: 'theme light dark appearance mode colour scheme match system night day',
    ways: [
      {
        via: 'With the palette',
        steps: 'Press ⌘K → run “Light”, “Dark” or “Match system”.',
      },
    ],
    group: 'Getting started',
  },
  {
    id: 'open-to',
    q: 'How do I change what the app opens to?',
    keywords: 'open to landing screen start home default my work board where i left off startup first screen',
    ways: [
      {
        via: 'In your account',
        steps:
          'Open your account (avatar, top right) → “Profile” → “Open to” → “My work”, “The board” or “Where I left off”. It is remembered per device.',
        target: { view: 'account', reveal: 'profile' },
      },
    ],
    group: 'Your account & sign-in',
  },
  {
    id: 'search-board',
    q: 'How do I search the board?',
    keywords: 'search board find card title filter top bar issues look for',
    ways: [
      {
        via: 'The top-bar search',
        steps:
          'On the Board (or Backlog), type in the “Search issues…” box in the top bar to filter the list by title or reference. (Press ⌘K to find cards, screens and help across the whole app.)',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'sort-board',
    q: 'How do I sort the cards in a column?',
    keywords: 'sort order column cards priority due date longest in progress recently updated arrange rank',
    ways: [
      {
        via: 'On the board',
        steps:
          'On the Board, “Sort” → “Priority”, “Due date”, “Longest in progress” or “Recently updated” orders the cards within every column.',
        target: { view: 'board', reveal: 'group-by' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'delete-card',
    q: 'How do I delete a card?',
    keywords: 'delete remove card issue bin discard restore undo trash',
    ways: [
      {
        via: 'In the open card',
        steps:
          'Open the card → “Edit” → “Delete” → confirm “Delete #ref?”. A copy is kept in Workspace → People & access → History, where an admin can restore it.',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'set-phase',
    q: 'How do I set a card’s phase?',
    keywords: 'phase stage card which phase plan stage set assign',
    ways: [
      {
        via: 'In the open card',
        steps: 'Open the card → “Edit” → “Phase” → “Save Changes”.',
        target: { view: 'board' },
      },
      {
        via: 'In the quick-add box',
        steps: 'When adding a card, type # and the phase (e.g. #build).',
        target: { view: 'board', reveal: 'quick-add' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'card-release-priority',
    q: 'How do I mark a card as must-have for the release?',
    keywords: 'for this release must should could won’t have moscow scope priority release must-have importance',
    ways: [
      {
        via: 'In the open card',
        steps:
          'Open the card → “Edit” → “Release necessity” → “Must have”, “Should have”, “Could have” or “Won’t have”. (This is how essential the item is for the release — day-to-day urgency is the separate “Priority”.)',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'card-labels',
    q: 'How do I add labels to a card?',
    keywords: 'labels tags label keyword card mark pick create colour chip',
    ways: [
      {
        via: 'In the open card',
        steps:
          'Open the card → “Edit” → “Labels” → type to pick from the board’s labels or create a new one in place; each carries its colour → “Save Changes”. Set up and colour labels in Settings → Workflow → Labels.',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
  {
    id: 'card-sprint',
    q: 'How do I put a card in a sprint?',
    keywords: 'sprint add card to iteration move assign card sprint backlog take out',
    ways: [
      {
        via: 'In the open card',
        steps:
          'Open the card → “Edit” → “Sprint” → pick a sprint (or “Backlog — in no sprint” to take it out) → “Save Changes”.',
        target: { view: 'board' },
      },
      {
        via: 'From the sprint',
        steps: 'Or open Sprints (under Work) → a sprint → “Add from the backlog” → find the card and “Add”.',
        target: { view: 'sprints' },
      },
    ],
    group: 'Backlog & sprints',
  },
  {
    id: 'manage-sprint',
    q: 'How do I edit, start or delete a sprint?',
    keywords: 'edit sprint start delete rename change dates remove sprint cancel manage',
    ways: [
      {
        via: 'On the Sprints screen',
        steps:
          'Open Sprints (under Work) → on a sprint, “Edit” changes its name, goal and dates; “Start it” begins a planned one; “Delete” (confirm “Delete <name>?”) removes a sprint that isn’t running. “Take out” removes a card from it.',
        target: { view: 'sprints' },
      },
    ],
    group: 'Backlog & sprints',
  },
  {
    id: 'edit-lesson',
    q: 'How do I edit or delete a lesson?',
    keywords: 'edit lesson delete remove change update correct lesson learned amend',
    ways: [
      {
        via: 'On the lesson',
        steps:
          'Open Lessons (under Work) → a lesson → “Edit” to change its kind, category, owner or text, or “Delete” (confirm “Delete this lesson?”). In the Lessons library, only the author or an admin can edit a workspace lesson.',
        target: { view: 'lessons' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'retire-phase',
    q: 'How do I remove or bring back a phase?',
    keywords: 'remove phase retire delete stage put back retired phases restore hide',
    ways: [
      {
        via: 'On the Plan',
        steps:
          'Open Plan (under Work) → a phase’s “Edit” → “Remove phase”. It stops being offered on new work; cards already in it stay. Bring it back from “Retired phases” below → “Put back”.',
        target: { view: 'plan', reveal: 'add-phase' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'retire-area-label',
    q: 'How do I remove or bring back an area or label?',
    keywords: 'remove area label retire delete put back restore retired bring back hide colour',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Workflow” tab → the area under “The parts of this product”, or the label under “Labels” → “Remove”. It stops being offered on new work; cards already carrying it keep it, so past figures still add up. Bring it back from the “Retired” list below → “Put back”.',
        target: { view: 'settings', reveal: 'classification' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'forecast-retention',
    q: 'How do I set a forecast or retention?',
    keywords: 'forecast at completion retention rate hold back budget estimate final cost retention percent eac',
    ways: [
      {
        via: 'In Budget & payments',
        steps:
          'Open Budget & payments (under Finances) → “Edit budget” → set “Forecast at completion” and “Retention rate (%)” → “Save budget”.',
        target: { view: 'budgetpay', reveal: 'contract-value' },
      },
      {
        via: 'From the Plan',
        steps: 'On the Plan, “+ Add a forecast” opens the same form.',
        target: { view: 'plan', reveal: 'budget' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'budget-split',
    q: 'How do I split the budget between Capex and Opex?',
    keywords: 'capex opex split classification budget capital operational pool breakdown divide',
    ways: [
      {
        via: 'In Budget & payments',
        steps:
          'Open Budget & payments (under Finances) → “Edit budget” → “Classification — capex / opex split” → set the Capex and Opex amounts → “Save budget”. (A single payment’s pool is set on the line instead.)',
        target: { view: 'budgetpay', reveal: 'contract-value' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'invoice-lifecycle',
    q: 'How do I move an invoice from received to paid?',
    keywords: 'invoice lifecycle received accept work approve authorise pay mark paid stages payment status supplier invoice attach',
    ways: [
      {
        via: 'In the invoice line',
        steps:
          'Open Budget & payments (under Finances) → open the line → work down the footer: “Mark received”, “Accept work”, then “Approve for payment” (or “Send for authorisation” / “Authorise payment” when a chain governs), then “Mark paid”. Attach the bill with “Attach the supplier invoice (PDF)”.',
        target: { view: 'budgetpay' },
      },
    ],
    group: 'Planning & budget',
  },
  {
    id: 'supersede-document',
    q: 'How do I replace a signed document with a new version?',
    keywords: 'supersede new version replace signed document reissue revise update locked amend',
    ways: [
      {
        via: 'On the document',
        steps:
          'Open Documents (under Deliver) → a signed document → “Supersede with a new version”. The old one is kept; the new draft goes through sign-off again.',
        target: { view: 'documents' },
      },
    ],
    group: 'Documents & sign-off',
  },
  {
    id: 'withdraw-document',
    q: 'How do I take back or delete a document?',
    keywords: 'withdraw take back draft rejected delete document request cancel reopen remove',
    ways: [
      {
        via: 'On the document',
        steps:
          'Open Documents (under Deliver) → the document → “Take it back to a draft” (a rejected one), “Withdraw the request” (one out for signature), or “Delete this draft” / “Delete this document”.',
        target: { view: 'documents' },
      },
    ],
    group: 'Documents & sign-off',
  },
  {
    id: 'send-signer-link',
    q: 'How do I send a signer who has no account a link?',
    keywords: 'send link signer external no account email sign-off signature copy the link invite approver outside',
    ways: [
      {
        via: 'On the signature sheet',
        steps:
          'Open the document (or release) that is out for sign-off → on a signatory’s row, “Send a link” (then “Send the link again”), or “Copy the link” to share it yourself. They sign without an account.',
        target: { view: 'documents' },
      },
    ],
    group: 'Documents & sign-off',
  },
  {
    id: 'deliverables-signoff',
    q: 'How do I get a sprint’s deliverables signed off?',
    keywords: 'deliverables sign-off sprint authorisation create approve work done sprint signoff acceptance',
    ways: [
      {
        via: 'From the sprint',
        steps:
          'Open Sprints (under Work) → a sprint → the “Sign-off” panel → “Create the deliverables sign-off”, then send it for sign-off.',
        target: { view: 'sprints' },
      },
    ],
    group: 'Documents & sign-off',
  },
  {
    id: 'pull-release',
    q: 'How do I pull a release back?',
    keywords: 'pull release take back withdraw undo put back reinstate rollback recall unship',
    ways: [
      {
        via: 'On the release',
        steps: 'Open Releases (under Deliver) → a release → “Take it back” to pull it; “Put it back” reinstates it.',
        target: { view: 'releases' },
      },
    ],
    group: 'Releases',
  },
  {
    id: 'remove-person',
    q: 'How do I remove someone from the workspace?',
    keywords: 'remove person delete someone revoke access leaver offboard take out workspace member off-board',
    ways: [
      {
        via: 'On a person',
        steps:
          'Open Workspace (under Team) → “People & access” → “People” → “Manage” on the person → “Remove from workspace” → confirm (admins only). To remove them from one board only, use “Boards they can open” instead.',
        target: { view: 'admin' },
      },
    ],
    group: 'People & workspace',
  },
  {
    id: 'view-as',
    q: 'How do I preview what someone else can see?',
    keywords: 'view as preview access impersonate see as role check what they can do test permissions',
    ways: [
      {
        via: 'On a person',
        steps:
          'Open Workspace (under Team) → “People & access” → “People” → “Manage” on the person → “View as” to see FlightDeck with their access (not offered for admins). Everything you do still goes out as you.',
        target: { view: 'admin' },
      },
    ],
    group: 'People & workspace',
  },
  {
    id: 'find-setting',
    q: 'How do I find a setting?',
    keywords: 'find setting search settings box jump control which tab locate where is',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Find a setting” box under the tabs → type what you’re after (e.g. “contract value”, “phases”, “export”) → pick it to jump to the right tab and control.',
        target: { view: 'settings' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'setup-project',
    q: 'How do I run the project setup questions again?',
    keywords: 'set up project setup questions wizard onboarding go through configure again walkthrough',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Project” tab → “Set this project up” → “Go through the questions” — a quick wizard covering the name, issue source, code location, “done”, you, team access, assistants and notifications.',
        target: { view: 'settings', reveal: 'setup' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'project-key',
    q: 'How do I set the project key or code repository?',
    keywords: 'project key prefix code repo repository github where the code lives commit link url short',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Project” tab → the “Project” panel → “Project key” (a short prefix, e.g. ORB) and “Where the code lives” (a repo URL that turns a commit mentioned on a card into a link).',
        target: { view: 'settings', reveal: 'project-name' },
      },
    ],
    group: 'Board setup',
  },
  {
    id: 'export-backup',
    q: 'How do I export a backup?',
    keywords: 'export backup json download data save copy archive dump',
    ways: [
      {
        via: 'In Settings',
        steps:
          'Open Settings → the “Releases & data” tab → the “Data” panel → “Export backup (JSON)” downloads the board’s data.',
        target: { view: 'settings', reveal: 'export' },
      },
    ],
    group: 'Board setup',
  },
  // ——— Run vs Change (#435) ———
  {
    id: 'run-vs-change',
    q: 'How do I see how much of our work is building new things vs keeping the lights on?',
    keywords: 'run change investment bau business as usual build keep lights running split work mix portfolio capex opex maintenance feature',
    ways: [
      {
        via: 'On the board',
        steps:
          'Every card is read as Change (building something new — a feature or improvement) or Run (keeping the service running — a fix or upkeep), from its type. The board metrics strip shows this board’s split as a “Work Mix” tile and a one-word chip (Change / Run / Mixed).',
        target: { view: 'board', reveal: 'work-mix' },
      },
      {
        via: 'Across the whole portfolio',
        steps:
          'Open Portfolio → Projects. The band at the top sums every board into one Change-vs-Run split by card count, and each board shows its own mix in the “Mix” column — so you can see at a glance which boards are investment-heavy and which are pure run.',
        target: { view: 'portfolio', reveal: 'portfolio-mix' },
      },
    ],
    group: 'Reporting & insights',
  },
  {
    id: 'reclassify-run-change',
    q: 'How do I change whether a card counts as Change or Run?',
    keywords: 'investment class override reclassify change run card funded differently reset auto type build maintain bau',
    ways: [
      {
        via: 'On the card',
        steps:
          'It is set on each card, not from the metrics. On the board, open any card → the “Investment class” field (under Type). It follows the card’s type by default; pick Change or Run to override one card that is funded differently, or “Reset to auto” to follow the type again. The board and portfolio splits update automatically.',
        target: { view: 'board' },
      },
    ],
    group: 'The board & cards',
  },
];

/** The Help screen's groups, in the order a newcomer meets them. */
export const HELP_GROUPS = [
  'Getting started',
  'Your account & sign-in',
  'The board & cards',
  'Backlog & sprints',
  'Planning & budget',
  'Documents & sign-off',
  'Releases',
  'Reporting & insights',
  'People & workspace',
  'Board setup',
] as const;

const norm = (s: string) => s.trim().toLowerCase();

/**
 * A newcomer types the verb they know, not the one the corpus authored ("make a
 * task" for "add a card"). A SMALL synonym map maps the common ones onto the
 * authored word so the search still finds the answer. Keep it short and obvious —
 * it is a handful of well-worn equivalents, not a thesaurus.
 */
const SYNONYMS: Record<string, string> = {
  make: 'add',
  create: 'add',
  new: 'add',
  task: 'card',
  ticket: 'card',
  remove: 'delete',
};

/**
 * The forms of one query word we accept as a hit: the word itself, its singular
 * (drop a trailing "s", so "cards" finds "card" and "signs" finds "sign"), and any
 * synonym. Deliberately light — enough for natural phrasing, not a real stemmer.
 */
const formsOf = (w: string): string[] => {
  const out = [w];
  if (w.length > 3 && w.endsWith('s')) out.push(w.slice(0, -1));
  if (SYNONYMS[w]) out.push(SYNONYMS[w]);
  return out;
};

/**
 * Words too common to narrow a search. A query made ONLY of these is treated as
 * having no search words, so it falls back to the substring tiers rather than
 * matching everything.
 */
const STOP_WORDS = new Set([
  'how', 'do', 'i', 'to', 'a', 'an', 'the', 'my', 'of', 'in', 'on', 'is', 'can', 'me', 'with', 'for', 'and', 'or',
]);

/** The meaningful words of a query — lower-cased, stop-words dropped. */
const searchWords = (q: string): string[] => (q.match(/[a-z0-9]+/g) ?? []).filter((w) => !STOP_WORDS.has(w));

/**
 * The how-tos matching a query, best first. Four tiers, each a weaker match than
 * the last, with no answer repeated:
 *   1. the question text STARTS with the whole query,
 *   2. the question text CONTAINS the whole query,
 *   3. the keywords CONTAIN the whole query,
 *   4. EVERY meaningful word of the query appears in the question or keywords, in
 *      any order (with light singular/synonym forms).
 * Tier 4 is why a natural phrasing — "create a card", "who signs off", "make a
 * task" — finds its answer even though the exact string is not one contiguous run
 * (learnability-witness F-A, 15 Oct 2026). The first three tiers keep exact and
 * prefix matches ranked above the looser word match, so a precise query is
 * unchanged. An empty query returns the whole corpus in authored order (the Help
 * screen's index). The palette uses this for its `help` results.
 */
export function howTosFor(query: string): HowTo[] {
  const q = norm(query);
  if (!q) return HOW_TO;
  const inStart = (h: HowTo) => norm(h.q).startsWith(q);
  const inQ = (h: HowTo) => norm(h.q).includes(q);
  const inKw = (h: HowTo) => norm(h.keywords).includes(q);
  const terms = searchWords(q);
  const allWords = (h: HowTo) => {
    if (terms.length === 0) return false;
    const hay = `${norm(h.q)} ${norm(h.keywords)}`;
    return terms.every((w) => formsOf(w).some((f) => hay.includes(f)));
  };
  const seen = new Set<string>();
  const out: HowTo[] = [];
  const add = (list: HowTo[]) => {
    for (const h of list)
      if (!seen.has(h.id)) {
        seen.add(h.id);
        out.push(h);
      }
  };
  add(HOW_TO.filter(inStart));
  add(HOW_TO.filter(inQ));
  add(HOW_TO.filter(inKw));
  add(HOW_TO.filter(allWords));
  return out;
}
